import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  Form,
  Input,
  message,
  Tabs,
  Alert,
  Table,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import {
  getApp,
  updateApp,
  listPlatformBindings,
  type App,
  type PlatformBinding,
} from '@/api/apps';
import { formatDateTime } from '@/utils/format';
import { CopyEditor } from './CopyEditor';
import { FlowEditor } from './FlowEditor';
import { AppPromptsEditor } from './AppPromptsEditor';
import { HomeLayoutEditor } from './HomeLayoutEditor';

const isNew = (id: string | undefined) => id === 'new';

export function AppDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<App | null>(null);
  const [bindings, setBindings] = useState<PlatformBinding[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    if (!id || isNew(id)) return;
    setLoading(true);
    try {
      const a = await getApp(Number(id));
      setApp(a);
      form.setFieldsValue({
        name: a.name,
        icon_url: a.icon_url ?? '',
      });
      const b = await listPlatformBindings(a.id);
      setBindings(b.data);
    } catch (e) {
      message.error(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    const values = await form.validateFields();
    if (!app) return;
    setSaving(true);
    try {
      const updated = await updateApp(app.id, values);
      setApp(updated);
      message.success('已保存');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  if (isNew(id)) {
    return (
      <Alert
        type="info"
        showIcon
        message="新建 App 功能"
        description={
          <Space direction="vertical">
            <span>POST /admin/v1/apps 需要 slug + name；目前请通过后端 curl 创建草稿后再来这里编辑。</span>
            <Button onClick={() => navigate('/apps')}>返回列表</Button>
          </Space>
        }
      />
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/apps')}>
          返回列表
        </Button>
      </Space>

      {loading || !app ? (
        <Skeleton active />
      ) : (
        <Tabs
          defaultActiveKey="basic"
          items={[
            {
              key: 'basic',
              label: '基础信息',
              children: (
                <Card
                  extra={
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleSave}
                      loading={saving}
                    >
                      保存
                    </Button>
                  }
                >
                  <Descriptions column={2} size="small" style={{ marginBottom: 16 }}>
                    <Descriptions.Item label="App ID">{app.id}</Descriptions.Item>
                    <Descriptions.Item label="slug">
                      <Tag>{app.slug}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="状态">
                      <Tag color="green">{app.status}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="创建时间">
                      {formatDateTime(app.created_at)}
                    </Descriptions.Item>
                  </Descriptions>
                  <Form form={form} layout="vertical">
                    <Form.Item
                      name="name"
                      label="App 名称"
                      rules={[{ required: true, message: '请输入名称' }, { max: 64 }]}
                    >
                      <Input maxLength={64} />
                    </Form.Item>
                    <Form.Item name="icon_url" label="图标 URL">
                      <Input placeholder="https://cdn.touliu.cn/..." />
                    </Form.Item>
                  </Form>
                </Card>
              ),
            },
            {
              key: 'copy',
              label: 'Copy 编辑',
              children: <CopyEditor app={app} onSaved={setApp} />,
            },
            {
              key: 'flow',
              label: '流程 (Flow)',
              children: <FlowEditor app={app} onSaved={setApp} />,
            },
            {
              key: 'home',
              label: '首页编排',
              children: <HomeLayoutEditor app={app} onSaved={setApp} />,
            },
            {
              key: 'prompts',
              label: 'LLM Prompts (App 覆盖)',
              children: <AppPromptsEditor app={app} />,
            },
            {
              key: 'platforms',
              label: '平台绑定',
              children: (
                <Card>
                  <Table
                    rowKey="platform"
                    pagination={false}
                    dataSource={bindings}
                    columns={[
                      { title: '平台', dataIndex: 'platform', render: (v) => <Tag>{v}</Tag> },
                      { title: '外部 AppID', dataIndex: 'external_appid' },
                      { title: '发布状态', dataIndex: 'publish_status' },
                      {
                        title: '更新时间',
                        dataIndex: 'updated_at',
                        render: (v) => formatDateTime(v),
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: 'config',
              label: 'Theme / Routes / Config',
              children: (
                <Card>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="深度编辑请用 PATCH /admin/v1/apps/:id；下方为只读 JSON 预览。"
                  />
                  <Tabs
                    type="card"
                    items={[
                      {
                        key: 'theme',
                        label: 'theme',
                        children: <JsonBlock value={app.theme} />,
                      },
                      {
                        key: 'routes',
                        label: 'routes',
                        children: <JsonBlock value={app.routes} />,
                      },
                      {
                        key: 'config',
                        label: 'config',
                        children: <JsonBlock value={app.config} />,
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: 'related',
              label: '关联资源',
              children: (
                <Card>
                  <Space direction="vertical">
                    <Link to={`/content?app_id=${app.id}`}>查看 App 下的内容套系 →</Link>
                    <Link to={`/orders?app_id=${app.id}`}>查看 App 下的订单 →</Link>
                  </Space>
                </Card>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <pre
      style={{
        background: '#f8fafc',
        padding: 12,
        borderRadius: 6,
        maxHeight: 320,
        overflow: 'auto',
        fontSize: 12,
        margin: 0,
      }}
    >
      {value ? JSON.stringify(value, null, 2) : '(empty)'}
    </pre>
  );
}
